import React from 'react';
import './css/index.css';
import { Assets, AssetWrapper, AssetHistoryDetail } from './components/Assets';
import { HomePage } from './components/homepage';
import { Breadcrumbs, Header } from './components/Header';
import { Members, MemberWrapper, MemberHistoryDetail } from './components/members';
import { DeviceSubTypes, DeviceSubTypeDetails } from "./components/DeviceSubTypes";
import { Readiness, ReadinessMenu } from './components/Readiness';
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { TeamHistoryDetail, Teams, TeamWrapper } from './components/teams';
import { NavigateWithId } from './components/common/NavigateWithId';
import { Onestop } from './components/Onestop/Onestop';
import { createContext } from 'react';
import {
    AuthProvider,
    ChangePassword,
    Login,
    PasswordReset,
    PasswordResetRequest,
} from './components/auth';
import { AuthorizedRoute, ProtectedRoute } from "./ProtectedRoute";

import "semantic-ui-css/semantic.min.css";
import "./css/App.css";
import { Checklist } from './components/DeviceSubTypes/Checklist';
import { useState } from 'react';
import { OnestopOverlay } from './components/Onestop/OnestopOverlay/OnestopOverlay';
import { AssetProblemIntakeForm } from './components/Assets/AssetProblemIntakeForm';
import { EventDetail, Events } from './components/Events';
import { AssetInspectionForm, AssetInspections } from "./components/AssetInspection";
import { MaintenancePage } from "./components/Maintenance";
import { MaintenanceDashboard } from "./components/Maintenance";
import { Forms } from './components/Forms';
import { UtilityStrike, UtilityStrikeFormDetails } from './components/Forms/UtilityStrike';
import { Questions, QuestionFormDetails } from './components/Forms/Questions';
import { JSAForms, JSAFormDetails } from './components/Forms/JSAForm';
import { RoadblockDashboard, RoadblockForm, RoadblockDetail, RoadblockTypeManager } from './components/Forms/Roadblock';
import { RoadblockReport } from './components/Roadblock';
import { FutureReadinessPage } from './components/FutureReadiness';
import { TeamCalendarView } from './components/TeamCalendar';
import { Documents, DocumentsHub } from './components/Documents';
import { VideoLibrary } from './components/VideoLibrary';
import { ContactsHub } from './components/Contacts';
import { SupportPage } from './components/Support';
import { DFNList } from './components/DFN';
import { FormChecklistList, FormChecklistDetail, FormChecklistForm } from './components/FormChecklists';
import { ManagerDashboard } from './components/ManagerDashboard';

export const GlobalContext = createContext(
    {
        showOnestopOverlay: false,
        setShowOnestopOverlay: () => { }
    }
)

export const App = () => {
    const [showOnestopOverlay, setShowOnestopOverlay] = useState(false)
    const globalContextValue = { showOnestopOverlay, setShowOnestopOverlay }

    const BaseLayout = () => {
        return (
            <GlobalContext.Provider value={globalContextValue} >
                <div className="App">
                    <Header />
                    <Breadcrumbs />
                    <Outlet />
                    {globalContextValue.showOnestopOverlay && <OnestopOverlay />}
                </div>
            </GlobalContext.Provider>
        )
    }
    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";


    const router = createBrowserRouter([
        {
            path: "/",
            element: <ProtectedRoute><BaseLayout /></ProtectedRoute>,
            handle: {
                crumbInfo: {
                    to: '/',
                    text: "Home"
                }
            },
            children: [
                {
                    path: "",
                    index: true,
                    element: <HomePage />
                },
                {
                    handle: {
                        crumbInfo: {
                            to: 'onestop',
                            text: 'Onestop'
                        }
                    },
                    path: "/onestop",
                    element: <AuthorizedRoute route='onestop'><Onestop /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            to: 'readiness',
                            text: 'Readiness'
                        }
                    },
                    path: "/readiness",
                    element: <AuthorizedRoute route='readiness'><Readiness /></AuthorizedRoute>
                },
                {
                    path: "/readinessreport",
                    children: [
                        {
                            path: "/readinessreport/team",
                            element: <AuthorizedRoute route='readinessReport'><ReadinessMenu type='team' /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    to: 'readinessreport/team',
                                    text: 'Readiness Report'
                                }
                            }
                        },
                        {
                            path: "/readinessreport/asset",
                            element: <AuthorizedRoute route='readinessReport'><ReadinessMenu type='asset' /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    to: 'readinessreport/asset',
                                    text: 'Readiness Report'
                                }
                            }
                        },
                        {
                            path: "/readinessreport/member",
                            element: <AuthorizedRoute route='readinessReport'><ReadinessMenu type='member' /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    to: 'readinessreport/member',
                                    text: 'Readiness'
                                }
                            }
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/maintenance',
                            text: 'Maintenance Dashboard'
                        }
                    },
                    path: "/maintenance",
                    element: <AuthorizedRoute route='maintenance'><MaintenanceDashboard /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/teams',
                            text: "Teams"
                        }
                    },
                    path: "/teams",
                    children: [
                        {
                            element: <AuthorizedRoute route='teams'><Teams /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/teams/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/teams/:id/view"
                                }
                            },
                            children: [
                                {
                                    path: "/teams/:id",
                                    element: <AuthorizedRoute route='teams'><NavigateWithId to={'/teams/:id/view'} /></AuthorizedRoute>,
                                },
                                {
                                    path: "/teams/:id/view",
                                    handle: {
                                        crumbInfo: {
                                            text: "Team Details",
                                            to: "/teams/:id/view",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='teams'><TeamWrapper chosenElement="View" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/teams/:id/changelog",
                                    handle: {
                                        crumbInfo: {
                                            text: "Team Details",
                                            to: "/teams/:id/changelog",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='teams'><TeamWrapper chosenElement="Changelog" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/teams/:id/changelog/:id2",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            },
                                            element: <AuthorizedRoute route='teams'><TeamHistoryDetail /></AuthorizedRoute>
                                        }
                                    ]
                                },
                                {
                                    path: "/teams/:id/calendar",
                                    handle: {
                                        crumbInfo: {
                                            text: "Team Details",
                                            to: "/teams/:id/calendar",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='teams'><TeamWrapper chosenElement="Calendar" /></AuthorizedRoute>,
                                            index: true
                                        }
                                    ]

                                }
                            ]
                        }
                    ]
                },

                {
                    handle: {
                        crumbInfo: {
                            to: '/members',
                            text: "Members"
                        }
                    },
                    path: "/members",
                    children: [
                        {
                            element: <AuthorizedRoute route='members'><Members /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/members/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/members/:id/view"
                                }
                            },
                            children: [
                                {
                                    path: "/members/:id",
                                    element: <AuthorizedRoute route='members'><NavigateWithId to={'/members/:id/view'} /></AuthorizedRoute>,
                                },
                                {
                                    path: "/members/:id/view",
                                    handle: {
                                        crumbInfo: {
                                            text: "Member Details",
                                            to: "/members/:id/view",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='members'><MemberWrapper chosenElement="View" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/members/:id/schedule",
                                    handle: {
                                        crumbInfo: {
                                            text: "Member Details",
                                            to: "/members/:id/schedule",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='members'><MemberWrapper chosenElement="Schedule" /></AuthorizedRoute>,
                                            index: true
                                        }
                                    ]
                                },
                                {
                                    path: "/members/:id/changelog",
                                    handle: {
                                        crumbInfo: {
                                            text: "Member Details",
                                            to: "/members/:id/changelog",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='members'><MemberWrapper chosenElement="Changelog" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/members/:id/changelog/:id2",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            },
                                            element: <AuthorizedRoute route='members'><MemberHistoryDetail /></AuthorizedRoute>
                                        }
                                    ]

                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/assets',
                            text: "Assets"
                        }
                    },
                    path: "/assets",
                    children: [
                        {
                            element: <AuthorizedRoute route='assets'><Assets /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/assets/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/assets/:id/view"
                                }
                            },
                            children: [
                                {
                                    path: "/assets/:id",
                                    element: <AuthorizedRoute route='assets'><NavigateWithId to={'/assets/:id/view'} /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/view",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/view",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="View" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/checklist",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/checklist",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Checklist" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/problems",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/problems",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Problems" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/assets/:id/problems/:id2",
                                            element: <AuthorizedRoute route='assets'><AssetProblemIntakeForm /></AuthorizedRoute>,
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            }
                                        }
                                    ]
                                },
                                {
                                    path: "/assets/:id/changelog",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/changelog",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Changelog" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/assets/:id/changelog/:id2",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            },
                                            element: <AuthorizedRoute route='assets'><AssetHistoryDetail /></AuthorizedRoute>
                                        }
                                    ]

                                },
                                {
                                    path: "/assets/:id/lifecycle",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/lifecycle",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Lifecycle" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/images",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/images",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Images" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/moves",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/moves",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Moves" /></AuthorizedRoute>,
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/devicesubtypes',
                            text: "Asset SubTypes"
                        }
                    },
                    path: "/devicesubtypes",
                    children: [
                        {
                            element: <AuthorizedRoute route='devicesubtypes'><DeviceSubTypes /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/devicesubtypes/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/devicesubtypes/:id"
                                }
                            },
                            children: [
                                {
                                    element: <AuthorizedRoute route='devicesubtypes'><DeviceSubTypeDetails /></AuthorizedRoute>,
                                    index: true,
                                },
                                {
                                    path: "/devicesubtypes/:id/checklist",
                                    element: <AuthorizedRoute route='devicesubtypes'><Checklist /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/',
                                            replacParam: "id",
                                            params: ["id"],
                                            text: "Checklist"
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/events',
                            text: "Events"
                        }
                    },
                    path: "/events",
                    children: [
                        {
                            element: <AuthorizedRoute route='events'><Events /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/events/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/events/:id"
                                }
                            },
                            element: <AuthorizedRoute route='events'><EventDetail /></AuthorizedRoute>
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/teamcalendar',
                            text: "Team Calendar"
                        }
                    },
                    path: "/teamcalendar",
                    element: <AuthorizedRoute route='teamcalendar'><TeamCalendarView /></AuthorizedRoute>
                },
                {
                    path: "/password-change",
                    element: <ChangePassword />
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/standdown',
                            text: 'Stand-Down'
                        }
                    },
                    path: "/standdown",
                    children: [
                        {
                            element: <AuthorizedRoute route='standdown'><AssetInspections /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/standdown/:id",
                            element: <AuthorizedRoute route='standdown'><AssetInspectionForm mode={"edit"} /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/standdown/:id/"
                                }
                            }
                        },
                        {
                            path: "/standdown/new",
                            element: <AuthorizedRoute route='standdown'><AssetInspectionForm mode={"create"} /></AuthorizedRoute>,

                        }
                    ]

                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/forms',
                            text: "Forms"
                        }
                    },
                    path: "/forms",
                    children: [
                        {
                            element: <AuthorizedRoute route='forms'><Forms /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "utilitystrike",
                            children: [
                                {
                                    element: <AuthorizedRoute route='forms'><UtilityStrike /></AuthorizedRoute>,
                                    index: true,
                                    handle: {
                                        crumbInfo: {
                                            to: '/forms/utilitystrike',
                                            text: 'Utility Strike'
                                        }
                                    }
                                },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='forms'><UtilityStrikeFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/forms/utilitystrike/new',
                                            text: 'New Utility Strike Form'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    element: <AuthorizedRoute route='forms'><UtilityStrikeFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ["id"],
                                            replaceParam: "id",
                                            to: "/forms/utilitystrike/:id",
                                            text: 'Utility Strike Form Details'
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            path: "jsaform",
                            handle: {
                                crumbInfo: {
                                    to: '/forms/jsaform',
                                    text: "JSA Forms"
                                }
                            },
                            children: [
                                {
                                    element: <AuthorizedRoute route='forms'><JSAForms /></AuthorizedRoute>,
                                    index: true
                                },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='forms'><JSAFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/forms/jsaform/new',
                                            text: 'New JSA Form'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    element: <AuthorizedRoute route='forms'><JSAFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ["id"],
                                            replaceParam: "id",
                                            to: "/forms/jsaform/:id",
                                            text: 'JSA Form Details'
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            path: 'questions',
                            handle: {
                                crumbInfo: {
                                    to: '/forms/questions',
                                    text: 'Questions Form',
                                },
                            },
                            children: [
                                {
                                    index: true,
                                    element: <AuthorizedRoute route='forms'><Questions /></AuthorizedRoute>,
                                },
                                {
                                    path: 'new',
                                    element: <AuthorizedRoute route='forms'><QuestionFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/forms/questions/new',
                                            text: 'New Question Form',
                                        },
                                    },
                                },
                                {
                                    path: ':id',
                                    element: <AuthorizedRoute route='forms'><QuestionFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ['id'],
                                            replaceParam: 'id',
                                            to: '/forms/questions/:id',
                                            text: 'Question Form Details',
                                        },
                                    },
                                },
                            ],
                        },
                        {
                            path: "roadblock",
                            children: [
                                {
                                    index: true,
                                    element: <AuthorizedRoute route='forms'><RoadblockDashboard /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/forms/roadblock',
                                            text: 'Roadblocks'
                                        }
                                    }
                                },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='forms'><RoadblockForm /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/forms/roadblock/new',
                                            text: 'New Roadblock Form'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    element: <AuthorizedRoute route='forms'><RoadblockDetail /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ['id'],
                                            replaceParam: 'id',
                                            to: '/forms/roadblock/:id',
                                            text: 'Roadblock Detail'
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/roadblock/report',
                            text: 'Roadblocks Report'
                        }
                    },
                    path: "/roadblock/report",
                    element: <AuthorizedRoute route='roadblock_report'><RoadblockReport /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/readiness/planning',
                            text: 'Future Readiness Planning'
                        }
                    },
                    path: "/readiness/planning",
                    element: <AuthorizedRoute route='future_readiness'><FutureReadinessPage /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/documents',
                            text: 'Documents'
                        }
                    },
                    path: "/documents",
                    children: [
                        {
                            index: true,
                            element: <AuthorizedRoute route='forms'><DocumentsHub /></AuthorizedRoute>
                        },
                        {
                            path: 'list',
                            element: <AuthorizedRoute route='forms'><Documents /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    to: '/documents/list',
                                    text: 'Documents'
                                }
                            }
                        },
                        {
                            path: 'video-content',
                            element: <AuthorizedRoute route='forms'><VideoLibrary /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    to: '/documents/video-content',
                                    text: 'Video Content'
                                }
                            }
                        },
                        {
                            path: "form-checklists",
                            handle: {
                                crumbInfo: {
                                    to: '/documents/form-checklists',
                                    text: 'Form Checklists'
                                }
                            },
                            children: [
                                { index: true, element: <AuthorizedRoute route='documents'><FormChecklistList /></AuthorizedRoute> },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='documents'><FormChecklistForm /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/documents/form-checklists/new',
                                            text: 'New Form Checklist'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    children: [
                                        {
                                            index: true,
                                            element: <AuthorizedRoute route='documents'><FormChecklistDetail /></AuthorizedRoute>,
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id"],
                                                    replaceParam: "id",
                                                    to: "/documents/form-checklists/:id",
                                                    text: 'Form Checklist Detail'
                                                }
                                            }
                                        },
                                        {
                                            path: "edit",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id"],
                                                    replaceParam: "id",
                                                    to: '/documents/form-checklists/:id/edit',
                                                    text: 'Edit Form Checklist'
                                                }
                                            },
                                            element: <AuthorizedRoute route='documents'><FormChecklistForm /></AuthorizedRoute>
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/contacts',
                            text: 'Contacts Hub'
                        }
                    },
                    path: "/contacts",
                    element: <ProtectedRoute><ContactsHub /></ProtectedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/manager/dashboard',
                            text: 'Manager Dashboard'
                        }
                    },
                    path: "/manager/dashboard",
                    element: <AuthorizedRoute route='manager_dashboard'><ManagerDashboard /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/support',
                            text: 'Support'
                        }
                    },
                    path: "/support",
                    children: [
                        {
                            element: <AuthorizedRoute route='support'><SupportPage /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    to: '/support/dfn',
                                    text: 'DFN'
                                }
                            },
                            path: "/support/dfn",
                            element: <AuthorizedRoute route='support'><DFNList /></AuthorizedRoute>
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    to: '/support/roadblock-types',
                                    text: 'Roadblock Types'
                                }
                            },
                            path: "/support/roadblock-types",
                            element: <AuthorizedRoute route='support'><RoadblockTypeManager /></AuthorizedRoute>
                        }
                    ]
                }
            ]
        },
        {
            path: "/login",
            element: <Login />
        },
        {
            path: "/password-reset",
            children: [
                {
                    element: <PasswordResetRequest />,
                    index: true
                },
                {
                    path: "/password-reset/:token",
                    element: <PasswordReset />,
                }
            ]
        }
    ]);

    return (
        <AuthProvider>
            {isMaintenanceMode ? <MaintenancePage /> : <RouterProvider router={router} />}
        </AuthProvider>
    )

}
